import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/constants/routes';
import { Banner } from '@/components/molecules/Banner';

/** Fondo del banner de test de nivel (host de assets de banners). */
const LEVEL_TEST_BANNER_BG = 'https://lab.knwy-tools.com/challenges/banners/BG_Test-Nivel.png';

/**
 * True cuando el alumno aún no ha COMPLETADO el test de nivel. Se basa en
 * `levelTestCompletedAt` (no en `level`): un alumno puede tener nivel asignado
 * por otras vías (backoffice, auto-selección en onboarding) sin haber hecho el
 * test. El backend solo setea ese campo desde el propio test de nivel.
 */
export function useLevelTestBannerVisible(): boolean {
  const { user } = useAuth();
  return user?.role === 'student' && !user.levelTestCompletedAt;
}

interface LevelTestBannerProps {
  /** Clases extra para espaciado/ajustes externos por página. */
  className?: string;
  /**
   * Dispara la coreografía de entrada del contenido (textos en cascada + CTA
   * con rebote). Por defecto `true` (anima al montar). El Dashboard lo pone a
   * `true` cuando termina la animación de entrada del contenedor.
   */
  reveal?: boolean;
}

/**
 * Banner que invita al alumno a hacer el test de nivel. Se auto-oculta
 * (devuelve `null`) cuando no procede, así que puede colocarse directamente
 * en cualquier página.
 */
export function LevelTestBanner({ className, reveal = true }: LevelTestBannerProps) {
  const { t } = useTranslation();
  const visible = useLevelTestBannerVisible();

  if (!visible) return null;

  return (
    <Banner
      title={t('dashboard.levelTestBannerTitle')}
      text={t('dashboard.levelTestBannerText')}
      to={ROUTES.STUDENT.LEVEL_TEST}
      backgroundImage={LEVEL_TEST_BANNER_BG}
      className={className}
      reveal={reveal}
    />
  );
}

export default LevelTestBanner;
